<template>
  <div class="edit-audit">
      <div>
            <!--
            Do modals once here instead of in each row to avoid having too
            many modals. Performance suffers once there a lot of modals
            -->
            <b-modal
                v-model="showAddItem"
                title="New Item"
                v-on:ok='saveNewItem()'
                size='lg'
                centered
            >
                <b-form-select v-model="newItem.type" :options="itemTypeOptions"/>
                <b-form-input
                    v-model="newItem.auditCode"
                    placeholder="Audit code"
                />
                <b-form-input
                    v-model="newItem.desc"
                    placeholder="Description"
                />
            </b-modal>
            <b-modal
                v-model="showAddItems"
                title="New Items"
                v-on:ok='saveNewItems()'
                size='lg'
                centered
            >
                Enter a list of checklist items, one per line (cut and paste from excel/google sheets should work)
                <b-form-textarea
                    v-model="newItems"
                    placeholder="Audit Code, Description"
                />
            </b-modal>

            <b-modal
                v-model="showEditItem"
                title="Edit"
                v-on:ok='saveItem()'
                size='lg'
                centered
            >
                <b-form-input
                    v-model="itemEdit.auditCode"
                />
                <b-form-textarea
                    type='text'
                    v-model='itemEdit.desc'
                    rows=6
                >
                </b-form-textarea>
            </b-modal>

            <b-modal
                v-model="showEditNote"
                title="Notes"
                v-on:ok='onSaveNote()'
                size='lg'
                centered
            >
                <b-form-textarea
                    type='text'
                    v-model='notesEdit'
                    rows=6
                >
                </b-form-textarea>
            </b-modal>


            <b-modal
                v-model="showDeleteQuestionModal"
                title="Delete"
                v-on:ok='onDeleteQuestion()'
                size='md'
                centered
            >
                This will delete this item and any recorded results from the audit. Are you sure you want to proceed?
            </b-modal>

            <b-modal
                v-model="showDeleteQuestionsModal"
                title="Delete Items"
                v-on:ok='onDeleteQuestions()'
                size='md'
                centered
            >
                This will delete the selected item(s) and any recorded results. Are you sure you want to proceed?
            </b-modal>


            <b-modal
                v-model="showDeleteModal"
                title="Delete"
                v-on:ok='onDeleteItem()'
                size='md'
                centered
            >
                This will delete this item and any recorded results from the audit. Are you sure you want to proceed?
            </b-modal>

            <b-modal
                v-model="showReviewersModal"
                :title="reviewersTitle"
                v-on:ok='onUpdateReviewers'
                v-on:cancel='onCancelReviewUpdate'
                size='lg'
                centered
                >
                <review-table :reviewers="reviewers"
                    :sharing="auditComplete"
                    v-on:deleteReviewer="onDeleteReviewer"
                    v-on:addReviewer="onAddReviewer"
                    v-on:reviewMessage="onReviewMessage"
                    v-on:sendReminder="onSendReminder">
                </review-table>
            </b-modal>

            <b-modal
                v-model="showImagePicker"
                title="Image Upload"
                size='lg'
                centered
                hide-footer
            >
                <amplify-photo-picker
                    v-bind:photoPickerConfig="photoPickerConfig"
                ></amplify-photo-picker>
            </b-modal>

            <b-modal
                v-model="showCompleteReviewAudit"
                title="Review Complete"
                size='lg'
                centered
                v-on:ok='onCompleteReview()'
                >
                This will complete the review of this audit and send it back to the auditor.
                Are you sure you want to proceed? 
            </b-modal>

            <b-modal
                v-model="showExportPDF"
                title="PDF Export"
                size = 'md'
                centered
                v-on:ok=exportAudit
                >
                <b-form-group>
                    <b-form-radio v-model="pdfFilter" name="pdfFilter-radios" value="all">Include all items</b-form-radio>
                    <b-form-radio v-model="pdfFilter" name="pdfFilter-radios" value="anyresult">Include only items with a result</b-form-radio>
                </b-form-group>
            </b-modal>

            <b-alert
                :show=tmplCrtdDismissCountDown
                dismissible
                @dismissed = "tmplCrtdDismissCountDown=0"
                @dismiss-count-down= "countDownChanged"
                fade
                >
                Created template "{{createdTemplate}}"
            </b-alert>
      </div>
      <div v-if="showAuthError">
          You do not have access to this audit. Please contact the auditor who sent you the link.
      </div>
      <b-card v-if="!showAuthError">
            <b-card-title>
                <b-container>
                    <b-row>
                        <b-col class="text-left">
                            Facility:
                        </b-col>
                        <b-col class="text-left">
                            <span 
                                class="auditFacNameEdit"
                                contenteditable 
                                v-text="audit.facilityName"
                                @blur="onEditFacName"
                                @keydown.enter="endEditFacName">
                            </span> 
                        </b-col>
                        <b-col class="text-right">
                            Audit Date:
                        </b-col>
                        <b-col class="text-left">
                            <span> {{audit.date}}</span>
                        </b-col>
                    </b-row>
                    <b-row v-if="audit.mgrName">
                        <b-col class="text-left">
                            Manager/Director:
                        </b-col>
                        <b-col class="text-left">
                            <span 
                                class="auditMgrNameEdit"
                                contenteditable
                                v-text="audit.mgrName"
                                @blur="onEditMgrName"
                                @keydown.enter="endEditMgrName">
                            </span>
                        </b-col>
                        <b-col></b-col>
                        <b-col></b-col>
                    </b-row>
                </b-container>
            </b-card-title>
            <b-card >
                <b-container fluid>
                    <b-row>
                        <b-col md="auto" v-if="isEditor">
                            <b-input-group>
                                <b-input-group-prepend>
                                    <b-button variant="primary">Save Audit</b-button>
                                    <b-button v-b-modal.save-as-template variant="primary">Save as Template</b-button>
                                </b-input-group-prepend>
                                <b-modal
                                    id="save-as-template"
                                    title="Save as Template"
                                    :ok-disabled='!templateNameState'
                                    v-on:ok='saveAsTemplate'>
                                    <b-card>
                                        <b-card-text>
                                            Save the Audit and its associated checklists as a template that can be used to create new audits.
                                        </b-card-text>
                                        <b-form-group
                                            id="save-as-template-form"
                                            >
                                            <b-form-group
                                                label="Template Name"
                                                label-for="template-name">
                                                <b-form-input
                                                    id="template-name"
                                                    v-model="templateInfo.name"
                                                    placeholder="Enter a name for your template..."
                                                    :state="templateNameState">
                                                </b-form-input>
                                            </b-form-group>
                                            <b-form-group
                                                label="Template Description"
                                                label-for="template-desc">
                                                <b-form-input
                                                    id="template-desc"
                                                    v-model="templateInfo.desc"
                                                    placeholder="Enter a description (optional)"></b-form-input>
                                            </b-form-group>

                                        </b-form-group>
                                    </b-card>
                                </b-modal>
                                <b-form-select
                                    v-model="auditStatus" :options="auditStatuses"
                                    v-on:input="changeStatus($event)"
                                >
                                </b-form-select>
                                <b-button v-if="auditInReview || auditComplete" @click="startReviewUpdate()">{{reviewersTitle}}</b-button>
                            </b-input-group>
                        </b-col>
                        <b-col v-if="!isReviewComplete && !isEditor">
                            <b-input-group>
                                <b-button variant="primary" @click="showCompleteReviewAudit=!showCompleteReviewAudit">Complete Review</b-button>
                            </b-input-group>
                        </b-col>
                        <b-col v-if="isEditor">
                            <b-input-group>
                                <b-button variant="primary" @click="showExportPDF=!showExportPDF">Export to PDF</b-button>
                            </b-input-group>
                        </b-col>
                    </b-row>
                </b-container>
            </b-card>
            <b-card>
                <attachements :reviewer="reviewer"></attachements>
            </b-card>
            <b-card
                title="Checklists"
            >
                <div v-if="isLoading">
                    <b-spinner></b-spinner>
                </div>
                <div v-if="!isLoading">
                    <b-modal
                        v-model="showAddChecklistModal"
                        title="New Checklist"
                        v-on:ok='saveNewChecklist'
                        size='lg'
                        centered
                    >
                        <b-form-input
                            v-model="newChecklist.auditCode"
                            placeholder="Audit code"
                        />
                        <b-form-input
                            v-model="newChecklist.desc"
                            placeholder="Title"
                        />
                    </b-modal>
                    <b-row>
                        <b-col>
                            <b-button @click="showAddChecklistModal = !showAddChecklistModal" v-if="isEditor">Add Checklist</b-button>
                        </b-col>
                        <b-col>
                            <div class="seach-box shadow">
                                <b-form-group>
                                    <div class="form-row">
                                        <b-input-group>
                                            <b-form-select
                                                class="form-control"
                                                label="Filter Results"
                                                v-model="resultsFilter" 
                                                :options="resultsFilterOptions"
                                            >
                                            </b-form-select>
                                            <b-input-group-append>
                                                <b-button @click="changeFilter">Filter Results</b-button>
                                            </b-input-group-append>
                                        </b-input-group>
                                    </div>
                                </b-form-group>
                            </div>
                        </b-col>
                    </b-row>
                    <div
                        role="tablist"
                        v-for="item in checklists"
                        :key="item.id"
                        class='list-item'>
                      <b-card no-body class="mb-1">
                        <checklist
                            :checklist="item"
                            :reviewer="reviewer"
                            :canEditReviews="canEditReviews"
                            :filter="resultsFilter"
                            v-on:deleteList=startDeleteItem
                            v-on:editDesc=startEditDesc
                            v-on:deleteQuestion=startDeleteQuestion
                            v-on:deleteQuestions=startDeleteQuestions
                            v-on:editListDesc=startEditListDesc
                            v-on:editNote=startEditNote
                            v-on:editImage=startEditImage
                            v-on:clearNote=onClearNote
                            v-on:addItem=startAddItem
                            v-on:addItems=startAddItems
                            >
                        </checklist>
                      </b-card>
                    </div>
                </div>
            </b-card>
        </b-card>
  </div>
</template>

<script>

    import Checklist from '@/components/Checklist.vue'
    import Reviewers from '@/components/Reviewers.vue'
    import Attachements from '@/components/Attachements.vue'

    import { v4 as uuidv4 } from 'uuid'
    import { mapGetters, mapState } from 'vuex'
    import { API } from 'aws-amplify'
    import { Auth, Storage } from 'aws-amplify'
    import { AmplifyEventBus } from 'aws-amplify-vue';

    import Papa from 'papaparse'
    import getPDFMake from './pdf/pdf.js'

    async function refreshCognitoToken(error, callback) {
        console.log("AuditError is ", error)
        if (error == "Unauthorized") {
            console.log("Refreshing token")
            const session = await Auth.currentSession()
            const user = await Auth.currentAuthenticatedUser()
            user.refreshSession(session.getRefreshToken(), callback)
        }
    }

    /**
     * Return true if the questions result is pass or fail
     */
    function questionHasResult(q) {
        return q.result
    }
    
    export default {
        name: 'Audit',
        props: [
            "audit",
            "reviewer",
        ],
        components: {
            "checklist": Checklist,
            "review-table": Reviewers,
            "attachements": Attachements
        },
        data() {
            return {
                "auditStatuses": [
                  {value: 'inprogress', text: 'In Progress'},
                  {value: 'in-review', text: 'In Review'},
                  {value: 'review-complete', text: 'Review Complete'},
                  {value: 'complete', text: 'Complete'}
                ],
                "newChecklist": {},
                "templateInfo": {
                    "name": '',
                    "desc": ''
                },
                "showAddChecklistModal": false,
                "descEdit": null,
                "listDescEdit": null,
                "currentQuestion": null,
                currentQuestions: null,
                showAuthError: false,
                showDeleteQuestionModal: false,
                showDeleteQuestionsModal: false,
                showDeleteModal: false,
                showEditItem: false,
                showEditNote: false,
                showExportPDF: false,
                showImagePicker: false,
                showReviewersModal: false,
                showAddItem: false,
                showAddItems: false,
                showCompleteReviewAudit: false,
                itemEdit: {
                    auditCode: "",
                    desc: ""
                },
                notesEdit: null,
                tmplCrtdDismissCountDown: 0,
                createdTemplate: "",
                resultsFilter: null,
                resultsFilterOptions: [
                    { value: null, text: "All"},
                    { value: "pass", text: "Passed"},
                    { value: "fail", text: "Failed"},
                    { value: "NA", text: "Not Assessed"}
                ],
                photoPickerConfig : {
                    path: 'images/',
                    defaultName: this.genUuid(),
                    storageOptions: {
                        level: "protected"
                    },
                    accept: "image/*"
                },
                'newItem': {
                    'type': 'checklistitem'
                },
                'newItems': "",
                'itemTypeOptions': [
                    { value: 'subcat', text: 'Sub-Category' },
                    { value: 'checklistitem', text: 'Checklist Item' },
                ],
                reviewMessage: null,
                pdfFilter: 'anyresult',
            }
        },
        computed: {
            auditStatus: function() {
                let state = this.audit.state
                if( state == 'in-review' && this.audit.reviewState =='complete') {
                    state = 'review-complete'
                }
                return state
            },
            reviewersTitle: function() {
                if (this.auditStatus == 'complete') {
                    return "Sharing..."
                }
                return "Reviewers..."
            },
            templateNameState: function() {
                return this.templateInfo.name.trim().length > 0
            },
            auditInReview: function() {
                return this.audit.state == "in-review"
            },
            auditComplete: function() {
                return this.audit.state == "complete"
            },
            ...mapGetters({
                isLoading: 'currentAudit/loading',
            }),
            ...mapState({
                reviewers: state => state.currentAudit.reviewers,
                auditError: state => state.currentAudit.error,
                ownerEmail: state => state.currentAudit.ownerEmail,
                userEmail: state => state.currentAudit.userEmail
            }),
            checklists: function() {
                let sub = this.$store.getters['currentAudit/filteredTopLevelChecklists']
                if( sub ) {
                    sub = sub.slice()
                    return sub.sort((a,b) => a.auditCode.localeCompare(b.auditCode))
                }
                return sub
            },
            isEditor: function() {
                return !this.reviewer
            },
            isReviewComplete: function() {
                return (this.auditStatus != "in-review") 
                    || (!this.isEditor && this.reviewers 
                    && this.reviewers.length > 0 && this.reviewers[0].state == "complete")
            },
            canEditReviews: function() {
                return this.auditStatus == "in-review" && 
                    this.reviewers.filter(r => r.currentUser)
                        .filter(r => r.role == "reviewer")
                        .length > 0
            }
        },
        watch: {
            auditError: {
                immediate: true,
                handler(newError, oldError) {
                    if(newError == "Unauthorized" ){
                        if(!oldError) {
                            refreshCognitoToken(newError, () => {
                                this.$emit('reloadAudit')
                            })
                        } else {
                            this.showAuthError = true
                        }
                    } else {
                        this.showAuthError = false
                    }
                }
            }        
        },
        methods: {
            genUuid: function () {
                const uuid = uuidv4();
                console.log("Unique id", uuid)
                return uuid
            },
            getS3ImageConf: function(img) {
                return  {
                    level: 'protected',
                    identityId: img.identityId
                }
            },
            changeFilter: function() {
                console.log("Changing filter to ", this.resultsFilter)
                this.$store.dispatch('currentAudit/setFilter', this.resultsFilter)
            },
            countDownChanged: function(dismissCountDown) {
                this.tmplCrtdDismissCountDown = dismissCountDown
            },
            endEditFacName: function() {
                this.$el.querySelector('.auditFacNameEdit').blur()
            },
            onEditFacName: async function(event) {
                let newFacName = event.target.innerText
                console.log("New Title was", newFacName)
                this.$store.dispatch('currentAudit/updateFacilityName', newFacName)
            },
            endEditMgrName: function() {
                this.$el.querySelector('.auditMgrNameEdit').blur()
            },
            onEditMgrName: async function(event) {
                let newMgrName = event.target.innerText
                console.log("New Manager was", newMgrName)
                this.$store.dispatch('currentAudit/updateMgrName', newMgrName)
            },
            onEditQuestion: async function(event, field, question, force) {
                if (question[field] != event || force) {
                    console.log('edit question', event, this.audit, question)

                    question[field] = event

                    this.audit.isDirty = true
                }
                else {
                    console.log(field, "already set to ", event)
                }
            },
            onSaveQuestion: async function() {
                console.log("saving audit?")
                if (this.audit.isDirty) {
                    console.log("Updating audit ", this.audit)
                    this.$emit('save-question', this.audit)
                    this.audit.isDirty = false
                }
            },
            onNewItem: function() {
                console.log("saving audit for new item")
                this.$emit('save-question', this.audit)
            },
            onEditList: async function(event, field, list) {
                console.log("editing list", list.desc)
                list.questions.forEach((q) => {
                    this.onEditQuestion(event, field, q);
                })
                this.onSaveQuestion(this.audit)
            },
            onDeleteItem: function() {
                this.$store.dispatch('currentAudit/deleteList', this.currentItem)
                this.showDeleteModal = false
            },
            startDeleteItem: function(item) {
                this.currentItem = item
                this.showDeleteModal = true
            },
            saveNewChecklist: function() {
                console.log("saving new checklist", this.newChecklist)
                this.newChecklist.id = uuidv4()
                this.newChecklist.audit = this.audit.id

                this.$store.dispatch('currentAudit/newChecklist', this.newChecklist)

                this.newChecklist = {}
            },
            startEditImage: function(question){
                console.log("editing image for ", question)
                this.photoPickerConfig.defaultName = this.genUuid()
                this.currentItem = question
                this.itemEdit = {
                    type: "question",
                    auditCode: question.auditCode,
                    imageData: question.imageData
                }
                AmplifyEventBus.$once('fileUpload', img => {
                    this.saveImage(img)
                })
                this.showImagePicker = true
            },
            saveImage: async function(imgUrl) {
                console.log("saving img for ", this.itemEdit, imgUrl)

                let { identityId } = await Auth.currentCredentials()

                let images = this.itemEdit.imageData ? [...this.itemEdit.imageData] : []
                images.push({
                    imagePath: imgUrl,
                    identityId: identityId
                })
                console.log("Saving images as", images)

                let fieldUpdates = []
                fieldUpdates.push({ field: 'imageData', value: images })
                this.$store.dispatch('currentAudit/updateQuestion', {item: this.currentItem, updates:fieldUpdates})

                this.currentItem = null
                this.showImagePicker = false
            },
            saveAsTemplate: function() {
                let templateName = this.templateInfo.name.trim()
                let templateObj = {
                    "name": templateName,
                    "desc": this.templateInfo.desc.trim(),
                    "audit": this.copyAudit(this.audit)
                }
                this.$emit('new-template', templateObj)
                this.templateInfo = {
                    "name": '',
                    "desc": ''
                }
                this.createdTemplate = templateName
                this.tmplCrtdDismissCountDown = 5
            },
            startEditListDesc: function(list) {
                this.currentItem = list
                this.itemEdit = {
                    type:"list",
                    auditCode:list.auditCode,
                    desc: list.desc
                }
                this.showEditItem = true;
            },
            saveItem: function() {
                let update;
                if(this.itemEdit.type == "list") {
                    update = 'currentAudit/updateList'
                } else if(this.itemEdit.type == "question") {
                    update = 'currentAudit/updateQuestion'
                }
                let fieldUpdates = []
                fieldUpdates.push({ field: 'desc', value: this.itemEdit.desc })
                fieldUpdates.push({ field: 'auditCode', value: this.itemEdit.auditCode})

                console.log("Saving item", this.currentItem, this.itemEdit, fieldUpdates, update)
                this.$store.dispatch(update, {item: this.currentItem, updates:fieldUpdates})
                this.itemEdit.auditCode = ""
                this.itemEdit.desc = ""
                this.itemEdit.type = ""
                this.currentItem = null
            },
            startEditDesc: function(question) {
                this.currentItem = question
                this.itemEdit = {
                    type:"question",
                    auditCode: question.auditCode,
                    desc: question.desc
                }
                this.showEditItem = true;
            },
            startDeleteQuestion: function(question) {
                this.currentQuestion = question;
                this.showDeleteQuestionModal = true;
            },
            startDeleteQuestions: function(questions) {
                this.currentQuestions = questions;
                this.showDeleteQuestionsModal = true;
            },
            onDeleteQuestion: function() {
                this.$store.dispatch('currentAudit/deleteQuestion', this.currentQuestion)
                this.currentQuestion = null
            },
            onDeleteQuestions: function() {
                this.$store.dispatch('currentAudit/deleteQuestions', this.currentQuestions)
                this.currentQuestion = null
            },
            startEditNote: function(question) {
                this.currentQuestion = question
                this.notesEdit = question.notes
                this.showEditNote = true
            },
            onSaveNote: function() {
                console.log("Saving ", this.notesEdit, this.currentQuestion)
                if(this.notesEdit == "") {
                    this.notesEdit = null
                }
                let updates = []
                updates.push({field:'notes', value:this.notesEdit})
                this.$store.dispatch('currentAudit/updateQuestion', {item:this.currentQuestion, updates})
                this.notesEdit = ""
                this.currentQuestion = null
            },
            onClearNote: function(question) {
                this.notesEdit = null
                this.currentQuestion = question
                this.onSaveNote()
            },
            startAddItem: function(checklist) {
                this.currentItem = checklist;
                this.showAddItem = true;
            },
            startAddItems: function(checklist) {
                this.currentItem = checklist;
                this.showAddItems = true;
            },
            saveNewItem: function() {
                console.log("saving new Item", this.newItem, " to ", this.checklist)

                let type = this.newItem.type

                this.newItem.type = undefined
                this.newItem.id = uuidv4()
                this.newItem.list = this.currentItem.id
                this.newItem.audit = this.audit.id

                if(type == "subcat") {
                    this.$store.dispatch('currentAudit/newChecklist', this.newItem)
                } else if (type == "checklistitem") {
                    this.$store.dispatch('currentAudit/newQuestion', this.newItem)
                }
                this.newItem = {
                    'type': 'checklistitem'
                }
                this.currentItem = null
            },
            saveNewItems: function() {
                console.log("Saving new items ")
                console.log(this.newItems)

                var results = Papa.parse(this.newItems, {delimiter: '\t'})
                //console.log("Results were")
                //console.log(results)

                let newItems = results.data.map((a) => {
                    return {
                        id: uuidv4(),
                        list: this.currentItem.id,
                        audit: this.audit.id,
                        auditCode: a[0],
                        desc: a[1]
                    }
                })

                newItems.map((i) => {
                    this.$store.dispatch('currentAudit/newQuestion', i)
                })
                this.currentItem = null
            },

            copyAudit: function() {
                let newAudit = {}
                Object.assign(newAudit, this.audit)
                newAudit.checklists = this.copyChecklists(this.$store.getters['currentAudit/topLevelChecklists'])
                return newAudit
            },
            copyChecklists: function(checklists) {
                return checklists.map(c => {
                    return this.copyChecklist(c)
                })
            },
            copyChecklist: function(checklist) {
                let newList = {}
                Object.assign(newList, checklist)
                let qs = this.$store.getters['currentAudit/questionsByParent'](checklist.id)
                newList.questions = qs.map(q => {
                    let newQ = {}
                    Object.assign(newQ, q)
                    return newQ
                })

                let sublists = this.$store.getters['currentAudit/checklistsByParent'](checklist.id)
                newList.sublists = this.copyChecklists(sublists)
                return newList
            },
            changeStatus(newStatus) {
                console.log("New status", newStatus)
                this.$store.dispatch('currentAudit/setState', newStatus)
            },

            /**
             * Reviewer related methods
             */
            startReviewUpdate: function(){
                this.reviewUpdates = {
                    delRevs: [],
                    newRevs: [],
                    sendReminder: false,
                }
                this.showReviewersModal = true
            },
            onReviewMessage: function(msg) {
                this.reviewMessage = msg
            },
            onUpdateReviewers: async function() {

                await Promise.all(this.reviewUpdates.delRevs.map(r => this.$store.dispatch('currentAudit/deleteReviewer', r)))
                await Promise.all(this.reviewUpdates.newRevs.map(r => this.$store.dispatch('currentAudit/addReviewer', r)))

                let emailUpdates;

                if( this.reviewUpdates.sendReminder) {
                    emailUpdates = this.reviewers.map(r => r.email);
                } else if( this.reviewUpdates.newRevs.length > 0) {
                    emailUpdates = this.reviewUpdates.newRevs.map(r => r.email);
                }
                if( emailUpdates && emailUpdates.length > 0) {
                    let apiName = "notifyReviewers"
                    let path = "/notify"
                    let myInit = {
                        body: {
                            audit: this.audit.id,
                            reviewers: emailUpdates,
                            facility: this.audit.facilityName,
                            msg: this.reviewMessage
                        }
                    }

                    API.post(apiName, path, myInit).then(response => {
                        console.log("Response from notify api", response)
                    }).catch(error => {
                        console.log("error invoking notify api", error)
                    });
                }
                this.reviewUpdates = null;
                this.reviewMessage = null;
            },
            onCancelReviewUpdate: async function() {
                this.reviewUpdates = null
            },
            onDeleteReviewer: async function(r) {
                let idx = this.reviewUpdates.newRevs.indexOf(r)
                if( idx != -1 ) {
                    // was added, so remove it from the new list
                    this.reviewUpdates.newRevs.splice(idx, 1)
                } else {
                    this.reviewUpdates.delRevs.push(r)
                }
            },
            onSendReminder: async function(r) {
                this.reviewUpdates.sendReminder = r;
            },
            onAddReviewer: async function(r, type) {
                let idx = this.reviewUpdates.delRevs.findIndex((rev) => rev.email == r)
                if (idx != -1) {
                    // was deleted, so remove it from the delete list
                    this.reviewUpdates.delRevs.splice(idx, 1)
                } else {
                    this.reviewUpdates.newRevs.push({email:r, role:type})
                }
            },

            onCompleteReview: async function() {
                if( this.audit.state == 'in-review' && !this.isReviewComplete) {
                    let user = await Auth.currentAuthenticatedUser();
                    let userEmail = user && user.attributes.email;
                    if(!userEmail) userEmail = undefined;

                    this.$store.dispatch('currentAudit/setReviewState', {newReviewState: 'complete', userEmail})
                    let apiName = "notifyReviewers"
                    let path = "/notify/reviewComplete"
                    let myInit = {
                        body: {
                            audit: this.audit.id,
                            auditors: [this.ownerEmail],
                            facility: this.audit.facilityName,
                            reviewerEmail: userEmail
                        }
                    }

                    API.post(apiName, path, myInit).then(response => {
                        console.log("Response from notify api", response)
                    }).catch(error => {
                        console.log("error invoking notify api", error)
                    });
                    this.$router.push("/reviews")
                } else {
                    console.log("Nothing to do")
                }
            },

            checklistToPdf: function(c, questionFilter, filterEmptyChecklists, imgs) {
                let qs = this.$store.getters['currentAudit/questionsByParent'](c.id)
                qs = qs.filter(questionFilter).map(q => mapQuestionToPDFRow(q, imgs))
                let sublists = this.$store.getters['currentAudit/checklistsByParent'](c.id)
                if(sublists) {
                    sublists = sublists.map(s => this.checklistToPdf(s, questionFilter, filterEmptyChecklists, imgs)).filter(c => c)
                }
                if (qs.length == 0 && sublists.length ==0 && filterEmptyChecklists){
                    // no sublists or questions so exclude the list
                    return null;
                }
                return [
                    { text: "\n" },
                    { text: c.auditCode + " " + c.desc, style: 'checklistHeader' },
                    {
                        layout: 'lightHorizontalLines',
                        table: {
                            headerRows: 1,
                            body: [
                                ['Audit Code', 'Description', 'Result', 'Notes', 'Action Plan Response Date', 'Response', 'Images'],
                                ...qs
                            ]
                        }
                    },
                    ...sublists 
                ]
            },
            exportAudit: async function() {
                console.log("exporting audit!", this.audit)

                try {

                    let pdfMake = getPDFMake();
                    let questionFilter = questionHasResult
                    let filterEmptyChecklists = true;
                    let imgs = [];
                    if (this.pdfFilter != "anyresult") {
                        questionFilter = () => true
                        filterEmptyChecklists = false;
                    }
                    let checklistEntries = this.checklists.map(c => this.checklistToPdf(c, questionFilter, filterEmptyChecklists, imgs))
                    let docDefinition = {
                        pageOrientation: 'landscape',
                        content: [
                            {text: this.audit.facilityName + " " + this.audit.date, style: 'auditHeader' },
                            {text:""},
                            ...checklistEntries
                        ],
                        images: {

                        },
                        styles: {
                            auditHeader: {
                                fontSize:22,
                                bold:true
                            },
                            checklistHeader: {
                                fontSize: 16,
                                bold: true
                            },
                            link: {
                                color: 'blue'  
                            }
                        }
                    };
                    await addImagesToPDF(docDefinition, imgs);
                    (await pdfMake).createPdf(docDefinition).open();
                
                } catch(err) {
                    console.log("Error generating PDF", err)
                    await this.$store.dispatch('currentAudit/recordError', {name:'exportPDF', error:err})
                }
            }

        },
    }
    async function addImagesToPDF(docDefinition, imgs) {
        let pdfImages = [];
        let actualImgs = {};
        for( let i = 0; i < imgs.length; ++i) {
            let imgData = imgs[i];

            try {
                let urls = await Promise.all(imgData.urls);
                //let urls = imgs.map(async(prom) => await prom)
                let imgObjs = urls.map((url, idx) => {
                    return {
                        image: 'img' + i + ':' + idx,
                        width: 400,
                        url: url
                    }
                });
                actualImgs = imgObjs.reduce((acc, current) => {
                    acc[current.image] = current.url
                    return acc
                }, actualImgs)
                pdfImages.push({
                    question: imgData.auditCode,
                    linkDestination: imgData.linkDestination,
                    urls: imgObjs
                })
            }catch(err) {
                console.log("Error adding image to PDF", err)
            }
        }
        console.log("pdfImages", pdfImages)
        console.log("actualImgs", actualImgs)
        if(pdfImages.length) {
            docDefinition.content.push({text: "Images", style: "auditHeader", pageBreak:"before"})
            for(let i = 0; i < pdfImages.length; ++i) {
                docDefinition.content.push({text: pdfImages[i].question, style: "checklistHeader", id:pdfImages[i].linkDestination})
                docDefinition.content.push(...(pdfImages[i].urls))
                docDefinition.content.push({text:"", pageBreak:"after"})
            }
            docDefinition.images = actualImgs;
        }
    } 
    function mapQuestionToPDFRow(q, imgs) {
        function undefToEmpty(s) {
            return s ? s : ""
        }
        let imgLink = "";
        if(q.imageData) {
            let urls = q.imageData.map((imgData) => getImgURL(q, imgData));
            let imageData = {
                auditCode: q.auditCode,
                desc: q.desc,
                linkDestination: 'IMGs' + q.id,
                urls: urls
            }
            imgs.push(imageData);
            imgLink = {text: "See Appendix", linkToDestination: imageData.linkDestination, style:"link"};
        }
        return [undefToEmpty(q.auditCode), 
            undefToEmpty(q.desc), 
            undefToEmpty(q.result), 
            undefToEmpty(q.notes), 
            undefToEmpty(q.completeDate), 
            undefToEmpty(q.response),
            undefToEmpty(imgLink)
            ];
    }

    function getImgURL(q, imgData) {
        console.log("Image Data", imgData)
        let opts = {

            level: 'protected',
            identityId: imgData.identityId
        };
        let url = Storage.get(imgData.imagePath, opts)
        console.log(url)
        return url;
    }

</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
    h3 {
        margin: 40px 0 0;
    }

    ul {
        list-style-type: none;
        padding: 0;
    }

    li {
        display: inline-block;
        margin: 0 10px;
    }

    a {
        color: #42b983;
    }
</style>
<style>
    .descriptionItem {
        max-width: 400px;
        text-align: left;
    }

    .resultItem {
        max-width: 100px;
    }

    .dueDateItem {
        max-width: 100px;
    }
</style>
